import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi, ApiError, type TileListResponse, type TileSetting } from '../api/client';
import './AppearanceSettings.css';

/**
 * Admin editor for the home-screen tile content at /admin/appearance.
 *
 * Each tile (Directory, Framework) renders as a card with photo preview,
 * file upload, title, description, save and clear-photo controls. Changes
 * persist via PUT /api/v1/admin/tiles/{tile_id}. The home page reads via
 * useTileSettings and falls back to i18n defaults when no override exists.
 *
 * Admin-only (admin.appearance permission, gated at the API).
 */

const EDITABLE_TILES = ['directory', 'framework'] as const;

type EditableTileId = typeof EDITABLE_TILES[number];

type FormState = {
  title: string;
  description: string;
  photoBase64: string | null;
  photoMime: string | null;
  photoPreviewUrl: string | null;
  serverPhotoUrl: string | null;
  pendingClear: boolean;
  saving: boolean;
  error: string | null;
  savedAt: number | null;
};

const emptyForm = (): FormState => ({
  title: '',
  description: '',
  photoBase64: null,
  photoMime: null,
  photoPreviewUrl: null,
  serverPhotoUrl: null,
  pendingClear: false,
  saving: false,
  error: null,
  savedAt: null,
});

export function AppearanceSettings() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [forms, setForms] = useState<Record<EditableTileId, FormState>>({
    directory: emptyForm(),
    framework: emptyForm(),
  });
  const [loadState, setLoadState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok' }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<TileListResponse>('/admin/tiles');
        if (cancelled) return;
        setForms((prev) => {
          const next = { ...prev };
          for (const tileId of EDITABLE_TILES) {
            const row = data.rows.find((r: TileSetting) => r.tile_id === tileId);
            next[tileId] = {
              ...emptyForm(),
              title: row?.title ?? '',
              description: row?.description ?? '',
              serverPhotoUrl: row?.photo_url ?? null,
            };
          }
          return next;
        });
        setLoadState({ kind: 'ok' });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Table not yet provisioned — fine, start with empty forms.
          setLoadState({ kind: 'ok' });
          return;
        }
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed to load tile settings';
        setLoadState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (tileId: EditableTileId, patch: Partial<FormState>) => {
    setForms((prev) => ({ ...prev, [tileId]: { ...prev[tileId], ...patch } }));
  };

  const handlePhotoChoose = (tileId: EditableTileId, file: File) => {
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      updateForm(tileId, { error: t('appearance.errInvalidType') });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      updateForm(tileId, { error: t('appearance.errTooLarge') });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      // result is a data URL like "data:image/jpeg;base64,XXXX..."
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : '';
      updateForm(tileId, {
        photoBase64: base64,
        photoMime: file.type,
        photoPreviewUrl: result,
        pendingClear: false,
        error: null,
      });
    };
    reader.onerror = () => {
      updateForm(tileId, { error: t('appearance.errReadFile') });
    };
    reader.readAsDataURL(file);
  };

  const handleClearPhoto = (tileId: EditableTileId) => {
    updateForm(tileId, {
      photoBase64: null,
      photoMime: null,
      photoPreviewUrl: null,
      pendingClear: true,
      error: null,
    });
  };

  const handleSave = async (tileId: EditableTileId) => {
    const form = forms[tileId];
    updateForm(tileId, { saving: true, error: null });
    try {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        description: form.description.trim(),
      };
      if (form.photoBase64 && form.photoMime) {
        body.photo_base64 = form.photoBase64;
        body.photo_mime = form.photoMime;
      } else if (form.pendingClear) {
        body.clear_photo = true;
      }
      await callApi(`/admin/tiles/${encodeURIComponent(tileId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      });
      updateForm(tileId, {
        saving: false,
        savedAt: Date.now(),
        photoBase64: null,
        pendingClear: false,
        serverPhotoUrl: form.photoBase64
          ? `/api/v1/tiles/${tileId}/photo?t=${String(Date.now())}`
          : form.pendingClear
            ? null
            : form.serverPhotoUrl,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${String(err.status)} — ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Save failed';
      updateForm(tileId, { saving: false, error: message });
    }
  };

  return (
    <article className="appearance">
      <header className="appearance__header">
        <h1 className="appearance__title">{t('appearance.title')}</h1>
        <p className="appearance__subtitle">{t('appearance.subtitle')}</p>
      </header>

      {loadState.kind === 'loading' && (
        <p className="appearance__status">{t('appearance.loading')}</p>
      )}
      {loadState.kind === 'error' && (
        <p className="appearance__status appearance__status--error">
          {loadState.message}
        </p>
      )}

      {loadState.kind === 'ok' && (
        <div className="appearance__grid">
          {EDITABLE_TILES.map((tileId) => {
            const form = forms[tileId];
            const previewSrc =
              form.photoPreviewUrl ??
              (form.pendingClear ? null : form.serverPhotoUrl);
            return (
              <section key={tileId} className="appearance-tile">
                <h2 className="appearance-tile__heading">
                  {t(`appearance.tile.${tileId}`)}
                </h2>

                <div className="appearance-tile__photo-row">
                  <div className="appearance-tile__preview">
                    {previewSrc ? (
                      <img
                        src={previewSrc}
                        alt=""
                        className="appearance-tile__preview-img"
                      />
                    ) : (
                      <span className="appearance-tile__preview-empty">
                        {t('appearance.noPhoto')}
                      </span>
                    )}
                  </div>
                  <div className="appearance-tile__photo-actions">
                    <label className="auth-button auth-button--secondary">
                      {t('appearance.choosePhoto')}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handlePhotoChoose(tileId, file);
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {previewSrc && (
                      <button
                        type="button"
                        className="auth-button auth-button--secondary"
                        onClick={() => handleClearPhoto(tileId)}
                      >
                        {t('appearance.clearPhoto')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="appearance-tile__field">
                  <label htmlFor={`appearance-${tileId}-title`}>
                    {t('appearance.field.title')}
                  </label>
                  <input
                    id={`appearance-${tileId}-title`}
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      updateForm(tileId, { title: e.target.value })
                    }
                    maxLength={200}
                    placeholder={t(`home.action.${tileId}.title`)}
                  />
                </div>

                <div className="appearance-tile__field">
                  <label htmlFor={`appearance-${tileId}-desc`}>
                    {t('appearance.field.description')}
                  </label>
                  <textarea
                    id={`appearance-${tileId}-desc`}
                    value={form.description}
                    onChange={(e) =>
                      updateForm(tileId, { description: e.target.value })
                    }
                    maxLength={500}
                    rows={3}
                    placeholder={t(`home.action.${tileId}.desc`)}
                  />
                </div>

                <div className="appearance-tile__actions">
                  <button
                    type="button"
                    className="auth-button auth-button--primary"
                    onClick={() => void handleSave(tileId)}
                    disabled={form.saving}
                  >
                    {form.saving ? t('appearance.saving') : t('appearance.save')}
                  </button>
                  {form.savedAt && (
                    <span className="appearance-tile__saved-badge">
                      {t('appearance.saved')}
                    </span>
                  )}
                  {form.error && (
                    <span className="appearance-tile__error">{form.error}</span>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}

export default AppearanceSettings;
